import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Box, Typography, TableBody, TableContainer, useTheme, useMediaQuery, Button, CircularProgress, Alert, IconButton, Paper, TextField, MenuItem, Autocomplete, TableFooter, Tooltip, Table, TableCell, TableHead, TableRow, Card, Rating, Checkbox, TableSortLabel, InputAdornment } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

// --- Status Mapping --- Moved from parent
const statusMap = {
    fetching: { text: 'Fetching', Icon: CircularProgress, color: 'primary.main', isSpinner: true, sortOrder: 1 },
    analyzing: { text: 'Processing', Icon: CircularProgress, color: 'primary.main', isSpinner: true, sortOrder: 2 },
    complete: { text: 'Monitored', Icon: CheckCircleIcon, color: 'success.main', sortOrder: 4 },
    failed: { text: 'Failed', Icon: ErrorIcon, color: 'error.main', sortOrder: 3 },
    // Default/fallback - should ideally not be hit if logic is correct
    default: { text: 'Monitored', Icon: CheckCircleIcon, color: 'success.main', sortOrder: 4 }
};

// --- Helper Function --- Moved from parent
const getStatusDisplay = (businessStatus, processingStatus) => {
    if (businessStatus === 'pending') {
        return statusMap.fetching; // Use the map entry directly
    }
    // Map the processing status
    switch (processingStatus) {
        case 'pending_analysis':
        case 'analyzing':
        case 'pending_grouping':
        case 'grouping':
            return statusMap.analyzing;
        case 'up_to_date':
            return statusMap.complete;
        case 'failed_analysis':
        case 'failed_grouping':
            return statusMap.failed;
        default: // null or undefined processingStatus means up-to-date/idle
            return statusMap.complete;
    }
};

// --- Sorting Helper for Status --- 
const getSortableStatusValue = (businessStatus, processingStatus) => {
    const statusInfo = getStatusDisplay(businessStatus, processingStatus);
    return statusInfo.sortOrder;
};

// Custom hook to measure container width and determine which columns to display - Moved from parent
const useResponsiveColumns = (containerRef) => {
  const theme = useTheme();
  // Use media queries for a more robust initial guess
  const initiallyShowBusinessType = useMediaQuery(theme.breakpoints.up('xs')); // ~400px
  const initiallyShowAddress = useMediaQuery(theme.breakpoints.up('xs')); // ~370px
  const initiallyShowRating = useMediaQuery(theme.breakpoints.up('xs')); // ~350px
  const initiallyShowTotalReviews = useMediaQuery(theme.breakpoints.up('sm')); // ~500px
  const initiallyShowRevenue = useMediaQuery(theme.breakpoints.up('sm')); // ~600px (Adjusted from md for Revenue)

  const [containerWidth, setContainerWidth] = useState(0); // Keep track of actual width
  // Initialize state based on media queries
  const [columns, setColumns] = useState({
    showBusinessType: initiallyShowBusinessType,
    showAddress: initiallyShowAddress,
    showRatingStars: initiallyShowRating,
    showTotalReviews: initiallyShowTotalReviews,
    showRevenue: initiallyShowRevenue,
  });

  // Effect to measure and update width using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    // Initial measurement
    updateWidth();

    // Setup ResizeObserver
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    // Cleanup
    return () => {
      const currentRef = containerRef.current;
      if (currentRef) {
         resizeObserver.unobserve(currentRef);
      }
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  // Effect to update column visibility based on *measured* width
  useEffect(() => {
    if (containerWidth > 0) {
      const newColumnState = {
        showBusinessType: containerWidth > 400,
        showAddress: containerWidth > 550,
        showRatingStars: containerWidth > 350,
        showTotalReviews: containerWidth > 650,
        showRevenue: containerWidth > 750,
      };
      setColumns(newColumnState);
    }
  }, [containerWidth]);

  return columns;
};

// --- Generic Sort Function --- 
function descendingComparator(a, b, orderBy) {
  let aValue = a[orderBy];
  let bValue = b[orderBy];

  // Handle nested properties if needed (e.g., orderBy = 'status.text')
  // This basic version assumes direct properties
  
  // Special handling for status sort order
  if (orderBy === 'statusSortValue') {
      aValue = getSortableStatusValue(a.status, a.processing_status);
      bValue = getSortableStatusValue(b.status, b.processing_status);
  } 
  // Handle numeric comparison explicitly for rating, reviews, turnover
  else if (['rating', 'total_reviews', 'turnover'].includes(orderBy)) {
      aValue = Number(aValue) || 0; // Default null/undefined to 0 for comparison
      bValue = Number(bValue) || 0;
  }
  // Handle string comparison (case-insensitive)
  else if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
  }
  // Handle primary_type specifically to format it consistently
  else if (orderBy === 'primary_type') {
      aValue = aValue ? aValue.replace(/_/g, ' ').toLowerCase() : '';
      bValue = bValue ? bValue.replace(/_/g, ' ').toLowerCase() : '';
  }

  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Stable sort preserves original order for equal elements
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1]; // Use original index as tie-breaker
  });
  return stabilizedThis.map((el) => el[0]);
}

// Define header cells configuration
const headCells = [
  { id: 'name', numeric: false, disablePadding: false, label: 'Name' },
  { id: 'primary_type', numeric: false, disablePadding: false, label: 'Type' },
  { id: 'address', numeric: false, disablePadding: false, label: 'Address' },
  { id: 'rating', numeric: true, disablePadding: false, label: 'Rating' },
  { id: 'total_reviews', numeric: true, disablePadding: false, label: 'Total Reviews' },
  { id: 'turnover', numeric: true, disablePadding: false, label: 'Revenue' },
  { id: 'statusSortValue', numeric: true, disablePadding: false, label: 'Status' }, // Use a dedicated sort key
];

const BusinessesTable = ({ 
  filteredBusinesses = [],
  filterText = '',
  onFilterChange,
  eligibleBusinesses = [],
  selectedIdsSet = new Set(),
  isAllSelected,
  isIndeterminate,
  subscriptionStatus,
  onStopMonitoringClick,
  onToggleSelect,
  onSelectAllClick,
  formatErrorMessage = (e) => String(e),
  API_URL,
  currencies = {},
  isSmallScreen,
  loading,
  isDemoMode = false,
}) => {

  const tableContainerRef = useRef(null);
  const { showBusinessType, showAddress, showRatingStars, showTotalReviews, showRevenue } = useResponsiveColumns(tableContainerRef);
  const theme = useTheme();

  // --- Sorting State (remains internal to table) ---
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  // --- Sort Request Handler (remains internal) ---
  const handleSortRequest = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const currencyList = Object.entries(currencies)
    .map(([code, details]) => ({ code, name: details.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // --- Memoized Filtered and Sorted Data ---
  const sortedBusinesses = useMemo(() => {
    // Input is already filtered, just apply sorting
    return stableSort(filteredBusinesses, getComparator(order, orderBy));
  }, [filteredBusinesses, order, orderBy]);

  // --- Render Logic --- 
  const canAddBusiness = subscriptionStatus?.is_active; // Use optional chaining
  const numEligible = eligibleBusinesses?.length ?? 0; // Use nullish coalescing

  // Calculate total columns for colSpan adjustments
  const numBaseCols = 1 + 1; // Name, Status (Checkbox is separate)
  const numOptionalCols = (showBusinessType ? 1 : 0) + (showAddress ? 1 : 0) + (showRatingStars ? 1 : 0) + (showTotalReviews ? 1 : 0) + (showRevenue ? 1 : 0);
  const totalCols = numBaseCols + numOptionalCols;

  // --- Main Component Return --- 
  return (
    <Box>
        {/* Filter Input */} 
        <Box sx={{ mb: 2, mt: 1, px: { xs: 1, md: 2 }, maxWidth: '400px' }}>
            <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Filter by business name..."
                value={filterText}
                onChange={onFilterChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                    endAdornment: filterText && (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="clear filter"
                                onClick={() => onFilterChange({ target: { value: '' } })}
                                edge="end"
                                size="small"
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                sx={{ 
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                    }
                 }}
            />
        </Box>
        
        <Card sx={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
          borderRadius: { xs: '12px', sm: '16px', md: '24px' },
          border: '1px solid rgba(25, 118, 210, 0.1)',
          boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
          }
        }}>
          {/* Display global loading indicator passed from parent */} 
          {loading && (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px'}}>
               <CircularProgress />
               <Typography sx={{ml: 2}}>Loading Businesses...</Typography>
            </Box>
          )}
          {/* Only render table if not in global loading state */} 
          {!loading && (
            <TableContainer ref={tableContainerRef} sx={{ margin: 0, padding: { xs: 0, md: 0 } }}>
              <Table 
                size="medium" 
                aria-label="businesses table"
                sx={{ 
                  tableLayout: 'fixed',
                  '& th, & td': {
                    px: { xs: 1, sm: 2 },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* Use onSelectAllClick prop */} 
                    <TableCell padding="checkbox" sx={{
                      width: '60px', // Fixed width
                      fontWeight: 700,
                      color: '#0A2540',
                      borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 },
                     }}>
                      <Checkbox
                        color="primary"
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={onSelectAllClick} // Use prop
                        disabled={numEligible === 0 || !(subscriptionStatus?.is_active)} // Also check subscription
                        inputProps={{ 'aria-label': 'select all businesses' }}
                      />
                    </TableCell>
                    {/* --- Table Headers (using showAddress etc. from local state) --- */} 
                    {headCells.map((headCell) => {
                        // Conditional rendering based on responsive state
                        if (headCell.id === 'primary_type' && !showBusinessType) return null;
                        if (headCell.id === 'address' && !showAddress) return null;
                        if (headCell.id === 'rating' && !showRatingStars) return null;
                        if (headCell.id === 'total_reviews' && !showTotalReviews) return null;
                        if (headCell.id === 'turnover' && !showRevenue) return null;
                        
                        // Determine width based on column ID
                        let cellWidth = 'auto';
                        if (headCell.id === 'name') cellWidth = { xs: '100px', sm: '130px', md: '180px' };
                        else if (headCell.id === 'primary_type') cellWidth = { xs: '90px', sm: '110px', md: '130px' };
                        else if (headCell.id === 'address') cellWidth = { xs: '120px', sm: '150px', md: '200px' };
                        else if (headCell.id === 'rating') cellWidth = { xs: '80px', sm: '120px' };
                        else if (headCell.id === 'total_reviews') cellWidth = { xs: '70px', sm: '100px' };
                        else if (headCell.id === 'turnover') cellWidth = { xs: '120px', sm: '140px' };
                        else if (headCell.id === 'statusSortValue') cellWidth = { xs: '80px', sm: '100px' }; // Adjusted status width

                        return (
                          <TableCell
                            key={headCell.id}
                            align={headCell.numeric ? 'center' : 'left'} // Center numeric, left align others
                            padding={headCell.disablePadding ? 'none' : 'normal'}
                            sortDirection={orderBy === headCell.id ? order : false}
                            sx={{
                              fontWeight: 700,
                              color: '#0A2540',
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                              width: cellWidth,
                              whiteSpace: (headCell.id === 'name' || headCell.id === 'address') ? 'normal' : 'nowrap', // Allow wrap for name/address
                              lineHeight: 1.2,
                              py: { xs: 1.5, sm: 2 },
                              px: { xs: 1, sm: 2 },
                              textAlign: headCell.id === 'statusSortValue' || headCell.id === 'rating' || headCell.id === 'total_reviews' ? 'center' : 'left',
                              cursor: 'pointer'
                            }}
                          >
                            <TableSortLabel
                              active={orderBy === headCell.id}
                              direction={orderBy === headCell.id ? order : 'asc'}
                              onClick={() => handleSortRequest(headCell.id)}
                            >
                              {headCell.label}
                              {orderBy === headCell.id ? (
                                <Box component="span" sx={{ position: 'absolute', top: -10, right: -10, opacity: 0 }}/> // Visually hidden accessibility text
                              ) : null}
                            </TableSortLabel>
                          </TableCell>
                        );
                    })}                    
                  </TableRow>
                </TableHead>
                
                <TableBody>
                  {/* --- Table Body Rendering --- */} 
                  {sortedBusinesses.length === 0 ? (
                     <TableRow>
                       <TableCell colSpan={totalCols + 1} align="center" sx={{ py: 4 }}>
                         <Typography sx={{ fontSize: '0.95rem', color: '#666666' }}>
                           {filterText ? 'No businesses match your filter.' : 'No businesses found. Add one below.'} 
                         </Typography>
                       </TableCell>
                     </TableRow>
                  ) : (
                    sortedBusinesses
                      .map((business) => {
                        const isSelected = selectedIdsSet.has(business.id);
                        const isMonitored = business.is_monitored;
                        const canInteract = subscriptionStatus?.is_active;

                        // Get status display using local helper
                        const statusDisplay = getStatusDisplay(business.status, business.processing_status);
                        const { Icon: StatusIcon, text: statusText, color: statusColor, isSpinner } = statusDisplay;

                        const handleRowClick = (event) => {
                            if (event.target.type === 'checkbox' || event.target.closest('button')) return;
                            // Allow selection only if active, monitored and processing complete/idle
                            const allowSelection = canInteract && isMonitored && 
                                (business.processing_status === 'up_to_date' || !business.processing_status);
                            if (allowSelection) {
                                onToggleSelect(business, isSelected); // Pass current state
                            }
                        };

                        // Disable checkbox if not active or not monitored
                        const disableSelectionCheckbox = !canInteract || !isMonitored;

                        return (
                          <TableRow
                            key={business.id}
                            hover={!disableSelectionCheckbox}
                            onClick={handleRowClick}
                            role="checkbox"
                            aria-checked={isSelected}
                            tabIndex={-1}
                            selected={isSelected}
                            sx={{
                              transition: 'all 0.2s ease-in-out',
                              cursor: !disableSelectionCheckbox ? 'pointer' : 'default',
                              opacity: canInteract ? 1 : 0.6,
                              '&:hover': {
                                  backgroundColor: !disableSelectionCheckbox ? 'action.hover' : undefined,
                                  transform: !disableSelectionCheckbox ? 'translateY(-2px)' : 'none'
                              },
                              '& td': {
                                borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
                                py: { xs: 1.5, sm: 2 },
                                px: { xs: 1, sm: 2 },
                                color: !canInteract ? '#9E9E9E' : undefined,
                              }
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={isSelected}
                                onChange={() => onToggleSelect(business, isSelected)}
                                disabled={disableSelectionCheckbox}
                                inputProps={{ 'aria-labelledby': `business-label-${business.id}` }}
                                onClick={(e) => e.stopPropagation()} 
                              />
                            </TableCell>
                            <TableCell sx={{
                              color: '#0A2540',
                              fontWeight: 600,
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              whiteSpace: 'normal',
                              overflow: 'hidden',
                              lineHeight: 1.2,
                              height: 'auto',
                            }}>
                              <Box sx={{
                                width: '100%',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                textOverflow: 'ellipsis',
                              }}>
                                {business.name}
                              </Box>
                            </TableCell>
                            
                            {showBusinessType && 
                              <TableCell sx={{
                                color: '#546E7A',
                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2,
                                textTransform: 'capitalize',
                              }}>
                                {business.primary_type 
                                  ? business.primary_type.replace(/_/g, ' ').toLowerCase()
                                  : 'N/A'}
                              </TableCell>
                            }
                            
                            {showAddress && 
                              <TableCell sx={{
                                color: '#546E7A',
                                fontSize: { xs: '0.875rem', md: '0.95rem' },
                                whiteSpace: 'normal',
                                overflow: 'hidden',
                                lineHeight: 1.2,
                                height: 'auto',
                              }}>
                                <Box sx={{
                                  width: '100%',
                                  overflow: 'hidden',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  textOverflow: 'ellipsis',
                                }}>
                                  {business.address}
                                </Box>
                              </TableCell>
                            }
                            
                            {showRatingStars && (
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Rating
                                    value={business.rating || 0}
                                    precision={0.1}
                                    readOnly
                                    size="small"
                                    sx={{ 
                                      color: '#1976d2',
                                      '& .MuiRating-iconEmpty': {
                                        color: 'rgba(25, 118, 210, 0.3)'
                                      }
                                    }}
                                  />
                                  <Typography sx={{ 
                                    ml: 1, 
                                    color: '#0A2540',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.875rem', md: '1rem' }
                                  }}>
                                    {business.rating?.toFixed(1) || 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )}
                            
                            {showTotalReviews && 
                              <TableCell sx={{ 
                                textAlign: 'center',
                                color: '#0A2540',
                                fontWeight: 500,
                                fontSize: { xs: '0.875rem', md: '1rem' }
                              }}>
                                  {business.total_reviews || 'N/A'}
                              </TableCell>
                            }
                            
                            {showRevenue && (
                              <TableCell sx={{
                                color: '#1976d2',
                                fontWeight: 600,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                              }}>
                                 {business.turnover !== null && business.turnover !== undefined 
                                   ? (() => {
                                       const currencyData = currencies[business.currency];
                                       const symbol = currencyData ? currencyData.symbol_native : '';
                                       return `${symbol}${Number(business.turnover).toLocaleString()}`;
                                     })()
                                   : 'N/A'}
                              </TableCell>
                            )}
                            
                            <TableCell padding="none" sx={{ width: 'auto', minWidth: '60px', textAlign: 'center', pr: { xs: 1, md: 2 } }}>
                              <Tooltip title={
                                isSpinner ? "Processing..." : 
                                isDemoMode ? "Demo mode - business removal disabled" :
                                (statusText || (isMonitored ? "Monitored. Click to remove." : "Not monitored"))
                              }>
                                {/* Render remove button if complete OR failed, and monitored */}
                                {!isSpinner && ( (statusDisplay.Icon === CheckCircleIcon || statusDisplay.Icon === ErrorIcon) && isMonitored ) ? (
                                  <span>
                                    <IconButton
                                      onClick={(e) => { e.stopPropagation(); onStopMonitoringClick(business); }}
                                      size="small"
                                      disabled={!canInteract || isDemoMode} // Disable button when subscription inactive OR demo mode
                                      sx={{
                                        padding: 0.5, 
                                        color: statusColor, 
                                        opacity: isDemoMode ? 0.5 : 1,
                                        '&:hover': {
                                          backgroundColor: (canInteract && !isDemoMode) ? 'action.hover' : 'transparent',
                                          color: (canInteract && !isDemoMode) ? theme.palette.success.dark : statusColor, 
                                        },
                                      }}
                                      aria-label="Remove business"
                                    >
                                      <StatusIcon sx={{ fontSize: '1.2rem' }} />
                                    </IconButton>
                                  </span>
                                ) : (
                                  // Otherwise, just display status icon/spinner (no text for consistency)
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {isSpinner ? (
                                      <CircularProgress size={18} sx={{ color: statusColor }} />
                                    ) : (
                                      <StatusIcon sx={{ fontSize: '1.1rem', color: statusColor }} />
                                    )}
                                  </Box>
                                )}
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
    </Box>
  );
};

export default BusinessesTable; 